import React, { useEffect, useState } from "react";
import { BACKEND_URL } from "@/lib/backendUrl";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Radio, Flame, Heart, Repeat2, Users, Music4, Play, Loader2, Crown, Mic } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const API = `${BACKEND_URL}/api`;

export default function AnalyticsPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [replays, setReplays] = useState([]);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) { navigate("/"); return; }
    axios.get(`${API}/me/analytics`)
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.detail || "Analytics failed"));
    axios.get(`${API}/live/replays`).then(r => setReplays(r.data || [])).catch(() => {});
  }, [user, navigate]);

  if (error) return (
    <div className="min-h-screen bg-[#0B0E14] text-white flex items-center justify-center px-6">
      <div className="text-center space-y-4 max-w-md" data-testid="analytics-locked">
        <Crown className="w-12 h-12 text-[#00F0FF] mx-auto"/>
        <div className="font-display font-black text-3xl">Analytics is PRO</div>
        <div className="text-[#94A3B8]">Upgrade to unlock listener, reaction and repost analytics for every set you publish.</div>
        <Button onClick={() => navigate("/studio")} className="bg-[#00F0FF] text-black hover:bg-[#33F3FF] font-black uppercase tracking-widest">Back to Studio</Button>
      </div>
    </div>
  );
  if (!data) return <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#00F0FF]"/></div>;

  const { totals, timeline, breakdown } = data;
  const maxPlays = Math.max(1, ...timeline.map(t => t.plays));

  const stats = [
    { label: "Sets", val: totals.sets, icon: Music4, color: "#00F0FF" },
    { label: "Plays", val: totals.plays, icon: Play, color: "#FF8C00" },
    { label: "Fire", val: totals.fire, icon: Flame, color: "#FF8C00" },
    { label: "Hearts", val: totals.heart, icon: Heart, color: "#FF2A6D" },
    { label: "Reposts", val: totals.reposts, icon: Repeat2, color: "#10B981" },
    { label: "Followers", val: totals.followers, icon: Users, color: "#00F0FF" },
  ];

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white" data-testid="analytics-page">
      <header className="h-16 border-b border-[#1E2638] px-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#00F0FF] to-[#FF2A6D] flex items-center justify-center"><Radio className="w-5 h-5 text-black" strokeWidth={2.5}/></div>
          <div className="font-display font-black text-xl tracking-tight">NEURAL<span className="text-[#00F0FF]">DECK</span></div>
        </Link>
        <Link to="/studio" className="font-mono text-xs uppercase tracking-widest text-[#94A3B8] hover:text-white">← Studio</Link>
      </header>
      <main className="max-w-6xl mx-auto px-6 py-10 space-y-10">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#00F0FF] mb-2 flex items-center gap-2"><Crown className="w-3 h-3"/> // PRO Analytics</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl">Your set performance</h1>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {stats.map(s => (
            <div key={s.label} className="rounded-xl border border-[#1E2638] bg-[#121620] p-4" data-testid={`stat-${s.label.toLowerCase()}`}>
              <div className="flex items-center gap-2 mb-2" style={{ color: s.color }}>
                <s.icon className="w-4 h-4"/>
                <span className="text-[10px] font-mono uppercase tracking-widest">{s.label}</span>
              </div>
              <div className="font-display font-black text-3xl">{s.val}</div>
            </div>
          ))}
        </div>

        {/* Timeline chart */}
        <div className="rounded-xl border border-[#1E2638] bg-[#121620] p-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00F0FF] mb-4">// Plays · last 30 days</div>
          <div className="flex items-end gap-1 h-40" data-testid="analytics-timeline">
            {timeline.map(t => (
              <div key={t.date} className="flex-1 flex flex-col items-center gap-1" title={`${t.date} · ${t.plays} plays`}>
                <div className="w-full rounded-t transition-colors"
                  style={{ height: `${(t.plays / maxPlays) * 100}%`, background: t.plays > 0 ? "linear-gradient(to top,#00F0FF,#FF2A6D)" : "#1E2638", minHeight: "3px" }}/>
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-2 font-mono text-[9px] text-[#94A3B8]">
            <span>{timeline[0]?.date}</span>
            <span>Today</span>
          </div>
        </div>

        {/* Per-set breakdown */}
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00F0FF] mb-3">// Sets breakdown</div>
          <div className="overflow-x-auto rounded-xl border border-[#1E2638]">
            <table className="w-full text-sm" data-testid="analytics-breakdown">
              <thead className="bg-[#1A202C]">
                <tr className="text-left text-[10px] font-mono uppercase tracking-widest text-[#94A3B8]">
                  <th className="p-3">Set</th>
                  <th className="p-3">Plays</th>
                  <th className="p-3">Fire</th>
                  <th className="p-3">Hearts</th>
                  <th className="p-3">Reposts</th>
                  <th className="p-3">Comments</th>
                </tr>
              </thead>
              <tbody>
                {breakdown.map(s => (
                  <tr key={s.id} className="border-t border-[#1E2638] hover:bg-[#1A202C]/50" data-testid={`row-${s.id}`}>
                    <td className="p-3">
                      <Link to={`/set/${s.id}`} className="font-bold hover:text-[#00F0FF] block truncate max-w-xs">{s.name}</Link>
                      <div className="text-[10px] font-mono text-[#94A3B8]">{new Date(s.created_at).toLocaleDateString()}</div>
                    </td>
                    <td className="p-3 font-mono">{s.plays}</td>
                    <td className="p-3 font-mono text-[#FF8C00]">{s.reactions.fire}</td>
                    <td className="p-3 font-mono text-[#FF2A6D]">{s.reactions.heart}</td>
                    <td className="p-3 font-mono text-[#10B981]">{s.reposts}</td>
                    <td className="p-3 font-mono">{s.comments}</td>
                  </tr>
                ))}
                {breakdown.length === 0 && (
                  <tr><td colSpan={6} className="p-6 text-center text-sm text-[#94A3B8]">No published sets yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        {/* Session Replays */}
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF2A6D] mb-3 flex items-center gap-2"><Mic className="w-3 h-3"/> // Live Session Recaps</div>
          {replays.length === 0 ? (
            <div className="rounded-xl border border-[#1E2638] bg-[#121620] p-6 text-sm text-[#94A3B8]" data-testid="replays-empty">
              No live sessions yet. Every broadcast auto-saves a recap when you end it (chat transcript, peak listeners, tips, merch sales).
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-[#1E2638]">
              <table className="w-full text-sm" data-testid="replays-table">
                <thead className="bg-[#1A202C]">
                  <tr className="text-left text-[10px] font-mono uppercase tracking-widest text-[#94A3B8]">
                    <th className="p-3">Date</th>
                    <th className="p-3">Duration</th>
                    <th className="p-3">Peak Listeners</th>
                    <th className="p-3">Tips</th>
                    <th className="p-3">Raised</th>
                    <th className="p-3">Merch Sold</th>
                  </tr>
                </thead>
                <tbody>
                  {replays.map(r => (
                    <tr key={r.id} className="border-t border-[#1E2638] hover:bg-[#1A202C]/50" data-testid={`replay-row-${r.id}`}>
                      <td className="p-3">
                        <div className="font-bold">{new Date(r.started_at).toLocaleDateString()}</div>
                        <div className="text-[10px] font-mono text-[#94A3B8]">{new Date(r.started_at).toLocaleTimeString()}</div>
                      </td>
                      <td className="p-3 font-mono">{Math.floor((r.duration_seconds || 0) / 60)}m {(r.duration_seconds || 0) % 60}s</td>
                      <td className="p-3 font-mono text-[#00F0FF]">{r.listeners_peak || 0}</td>
                      <td className="p-3 font-mono text-[#FFC107]">{r.tips_count || 0}</td>
                      <td className="p-3 font-mono text-[#10B981]">${((r.raised_cents || 0) / 100).toFixed(2)}</td>
                      <td className="p-3 font-mono">{r.merch_sales || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
